import React, { useState } from 'react';
import Modal from './Modal';
import { bookmarksApi } from '../api/client';

interface AddBookmarkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBookmarkAdded: () => void;
}

const AddBookmarkModal: React.FC<AddBookmarkModalProps> = ({ isOpen, onClose, onBookmarkAdded }) => {
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    description: '',
    category: 'reference' as 'work' | 'learning' | 'entertainment' | 'shopping' | 'reference' | 'personal',
    tags: '',
    isPrivate: false,
    isFavorite: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setFormData({
      title: '',
      url: '',
      description: '',
      category: 'reference',
      tags: '',
      isPrivate: false,
      isFavorite: false
    });
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateUrl = (url: string) => {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!formData.url.trim()) {
      setError('URL is required');
      return;
    }

    if (!validateUrl(formData.url)) {
      setError('Please enter a valid URL');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Ensure URL has protocol
      const normalizedUrl = formData.url.startsWith('http') 
        ? formData.url 
        : `https://${formData.url}`;

      const bookmarkData = {
        title: formData.title,
        url: normalizedUrl,
        description: formData.description,
        category: formData.category,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0),
        isPrivate: formData.isPrivate,
        isFavorite: formData.isFavorite
      };

      await bookmarksApi.createBookmark(bookmarkData);

      setSuccess(true);
      setTimeout(() => {
        onBookmarkAdded();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error adding bookmark:', err);
      setError('Failed to add bookmark. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Bookmark">
      <form onSubmit={handleSubmit} className="modal-form">
        {error && (
          <div className="modal-error">
            {error}
          </div>
        )}

        {success && (
          <div className="modal-success">
            Bookmark added successfully!
          </div>
        )}

        <div className="modal-form-group">
          <label>Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Page title or bookmark name"
            disabled={loading}
          />
        </div>

        <div className="modal-form-group">
          <label>URL *</label>
          <input
            type="text"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            placeholder="https://example.com or example.com"
            disabled={loading}
          />
        </div>

        <div className="modal-form-group">
          <label>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of this bookmark..."
            disabled={loading}
            rows={2}
          />
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
              disabled={loading}
            >
              <option value="work">Work</option>
              <option value="learning">Learning</option>
              <option value="entertainment">Entertainment</option>
              <option value="shopping">Shopping</option>
              <option value="reference">Reference</option>
              <option value="personal">Personal</option>
            </select>
          </div>

          <div className="modal-form-group">
            <label>Tags</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="tag1, tag2, tag3"
              disabled={loading}
            />
          </div>
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isFavorite}
                onChange={(e) => setFormData({ ...formData, isFavorite: e.target.checked })}
                disabled={loading}
                style={{ marginRight: '0.5rem' }}
              />
              Mark as favorite
            </label>
          </div>

          <div className="modal-form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isPrivate}
                onChange={(e) => setFormData({ ...formData, isPrivate: e.target.checked })}
                disabled={loading}
                style={{ marginRight: '0.5rem' }}
              />
              Keep private
            </label>
          </div>
        </div>

        <div className="modal-form-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-btn modal-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="modal-btn modal-btn-primary"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Bookmark'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddBookmarkModal;